import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApp } from '../context/AppContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { meses, anos } from '../data/mockData';

function Relatorio() {
  const { corretores, getMetaPorAno, calcularVGVTotal, calcularVGVMes } = useApp();
  const colors = useThemeColors();
  const [mesSelecionado, setMesSelecionado] = useState('Janeiro');
  const [anoSelecionado, setAnoSelecionado] = useState(2026);
  const [corretorSelecionado, setCorretorSelecionado] = useState('');

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = () => {
    return new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar apenas corretores ativos
  const corretoresAtivos = corretores.filter(c => c.ativo);

  // Helper function to get venda value (backward compatible)
  const getVendaValor = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    // Support both old format (number) and new format (object)
    return typeof venda === 'object' ? (venda.valor || 0) : venda;
  };

  // Helper function to get agenciamento value (integer)
  const getAgenciamento = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    // Support both old format (number) and new format (object)
    const agenciamento = typeof venda === 'object' ? (venda.agenciamento || 0) : 0;
    return parseInt(agenciamento, 10); // Ensure it's an integer
  };

  // Ordenar corretores por VGV do mês
  let corretoresComVendas = corretoresAtivos
    .map(corretor => {
      const vgv = getVendaValor(corretor, anoSelecionado, mesSelecionado);
      const agenciamento = getAgenciamento(corretor, anoSelecionado, mesSelecionado);
      return { ...corretor, vgv, agenciamento };
    })
    .filter(c => c.vgv > 0)
    .sort((a, b) => b.vgv - a.vgv);

  // Filtrar por corretor se selecionado
  if (corretorSelecionado) {
    corretoresComVendas = corretoresComVendas.filter(c => c.id === corretorSelecionado);
  }

  // Calcular totais
  const metaAnual = getMetaPorAno(anoSelecionado);
  // Se um corretor específico estiver selecionado, usar apenas os dados dele
  const acumulado = corretorSelecionado
    ? (corretoresComVendas[0]?.vgv || 0)
    : calcularVGVTotal(anoSelecionado);
  const vgvMes = corretorSelecionado
    ? (corretoresComVendas[0]?.vgv || 0)
    : calcularVGVMes(anoSelecionado, mesSelecionado);
  const percentualMeta = metaAnual > 0 ? (acumulado / metaAnual) * 100 : 0;
  const percentualMes = metaAnual > 0 ? (vgvMes / metaAnual) * 100 : 0;
  const mediaVGV = corretoresComVendas.length > 0 
    ? (corretorSelecionado 
        ? corretoresComVendas[0]?.vgv || 0
        : vgvMes / corretoresComVendas.length)
    : 0;
  const totalAgenciamentos = corretoresComVendas.reduce((sum, corretor) => sum + (corretor.agenciamento || 0), 0);

  // Calcular dados mensais para gráfico (quando corretor está selecionado)
  const dadosMensaisCorretor = useMemo(() => {
    if (!corretorSelecionado || corretoresComVendas.length === 0) return [];
    
    const corretor = corretoresComVendas[0];
    
    return meses.map(mes => {
      const vgvMes = getVendaValor(corretor, anoSelecionado, mes);
      
      return {
        mes: mes.substring(0, 3), // Abreviação do mês (Jan, Fev, etc)
        mesCompleto: mes,
        vgv: vgvMes,
      };
    });
  }, [corretorSelecionado, corretoresComVendas, anoSelecionado]);

  const top3 = corretoresComVendas.slice(0, 3);
  const restante = corretoresComVendas.slice(3);

  const getPosicaoBadgeColor = (posicao) => {
    // Usando cores do tema configurado
    if (posicao === 1) return colors.primary; // 1º lugar
    if (posicao === 2) {
      // 2º lugar - cor intermediária entre primary e secondary
      return colors.secondary;
    }
    if (posicao === 3) {
      // 3º lugar - cor mais suave
      return colors.secondary;
    }
    return colors.primary;
  };

  // Função para obter cor de fundo usando cores do tema
  const getCardBackground = () => {
    // Usa cores do tema configurado para os gradientes dos cards
    return colors.background.paper;
  };

  const getCardBackgroundReverse = () => {
    return colors.background.paper;
  };



  const handleExportarCSV = () => {
    // Prepare CSV data
    const csvRows = [];
    
    // Header - manter como arrays, não fazer join aqui
    csvRows.push(['Relatório de Vendas', mesSelecionado, anoSelecionado]);
    csvRows.push(['Gerado em', formatarData()]);
    csvRows.push([]); // Linha vazia
    
    // Summary data
    csvRows.push(['Resumo', '']);
    csvRows.push(['Meta Anual', formatarMoeda(metaAnual).replace(/[R$\s]/g, '')]);
    csvRows.push(['VGV Acumulado', formatarMoeda(acumulado).replace(/[R$\s]/g, '')]);
    csvRows.push(['VGV do Mês', formatarMoeda(vgvMes).replace(/[R$\s]/g, '')]);
    csvRows.push(['% da Meta', `${percentualMeta.toFixed(2)}%`]);
    csvRows.push(['% do Mês', `${percentualMes.toFixed(2)}%`]);
    csvRows.push(['Média VGV', formatarMoeda(mediaVGV).replace(/[R$\s]/g, '')]);
    csvRows.push([]); // Linha vazia
    
    // Table header
    csvRows.push(['Posição', 'Nome do Corretor', 'VGV', '% da Meta', 'Status']);
    
    // Table data
    corretoresComVendas.forEach((corretor, index) => {
      const posicao = index + 1;
      const percentualCorretor = metaAnual > 0 ? (corretor.vgv / metaAnual) * 100 : 0;
      const vgvFormatado = formatarMoeda(corretor.vgv).replace(/[R$\s]/g, '');
      
      csvRows.push([
        posicao.toString(),
        `"${corretor.nome}"`,
        vgvFormatado,
        `${percentualCorretor.toFixed(2)}%`,
        corretor.ativo ? 'Ativo' : 'Inativo'
      ]);
    });
    
    // Convert to CSV string - garantir que cada row seja um array antes de fazer join
    const csvContent = csvRows.map(row => {
      // Se row é um array vazio, retornar string vazia
      if (Array.isArray(row) && row.length === 0) {
        return '';
      }
      // Se row é um array, fazer join
      if (Array.isArray(row)) {
        return row.join(',');
      }
      // Se não for array, converter para string
      return String(row);
    }).join('\n');
    
    // Add BOM for UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_vendas_${mesSelecionado}_${anoSelecionado}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box 
      sx={{ 
        p: { xs: 2, md: 4 }, 
        background: colors.background.default,
        minHeight: '100vh',
        '@media print': {
          p: 2,
        }
      }}
      className="print-area"
    >
      {/* Header do Relatório */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: colors.background.paper,
          border: `1px solid ${colors.border.primary}`,
          '@media print': {
            mb: 2,
            p: 2,
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
             
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    '@media print': {
                      fontSize: '1.5rem',
                    }
                  }}
                >
                  Relatório de Vendas
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {mesSelecionado} {anoSelecionado}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              Gerado em {formatarData()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, '@media print': { display: 'none' } }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportarCSV}
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}10`,
                  transform: 'translateY(1px)',
                }
              }}
            >
              Exportar CSV
            </Button>
           
          </Box>
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper 
        sx={{ 
          p: 2.5, 
          mb: 3, 
          background: getCardBackground(),
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 3,
          '@media print': {
            display: 'none',
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mês</InputLabel>
              <Select
                value={mesSelecionado}
                label="Mês"
                onChange={(e) => setMesSelecionado(e.target.value)}
              >
                {meses.map((mes) => (
                  <MenuItem key={mes} value={mes}>
                    {mes}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Ano</InputLabel>
              <Select
                value={anoSelecionado}
                label="Ano"
                onChange={(e) => setAnoSelecionado(Number(e.target.value))}
              >
                {anos.map((ano) => (
                  <MenuItem key={ano} value={ano}>
                    {ano}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink={true}>Corretor</InputLabel>
              <Select
                value={corretorSelecionado}
                label="Corretor"
                onChange={(e) => setCorretorSelecionado(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{ color: 'text.secondary' }}>Todos os Corretores</Typography>;
                  }
                  const corretor = corretoresAtivos.find(c => c.id === selected);
                  return corretor ? corretor.nome : '';
                }}
              >
                <MenuItem value="">
                  <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Todos os Corretores</Typography>
                </MenuItem>
                {corretoresAtivos.map((corretor) => (
                  <MenuItem key={corretor.id} value={corretor.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        src={corretor.foto || undefined} 
                        alt={corretor.nome} 
                        sx={{ width: 24, height: 24 }}
                      >
                        {!corretor.foto && corretor.nome 
                          ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                          : ''}
                      </Avatar>
                      {corretor.nome}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Gráfico de Evolução Mensal (quando corretor está selecionado) */}
      {corretorSelecionado && dadosMensaisCorretor.length > 0 && (
        <Paper 
          sx={{ 
            background: getCardBackground(),
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 3,
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
              Evolução Mensal - {corretoresComVendas[0]?.nome || 'Corretor'} - {anoSelecionado}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Visualização da evolução do VGV mês a mês do ano {anoSelecionado}
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dadosMensaisCorretor}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border.primary} opacity={0.3} />
              <XAxis 
                dataKey="mes" 
                stroke={colors.text.secondary}
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis 
                stroke={colors.text.secondary}
                style={{ fontSize: '0.875rem' }}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `R$ ${(value / 1000).toFixed(0)}k`;
                  return `R$ ${value}`;
                }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: colors.background.paper,
                  border: `1px solid ${colors.border.primary}`,
                  borderRadius: 8,
                  color: colors.text.primary,
                }}
                formatter={(value) => formatarMoeda(value)}
                labelFormatter={(label) => {
                  const item = dadosMensaisCorretor.find(d => d.mes === label);
                  return item ? item.mesCompleto : label;
                }}
              />
              <Legend 
                wrapperStyle={{ color: colors.text.primary }}
              />
              <Line 
                type="monotone" 
                dataKey="vgv" 
                name="VGV"
                stroke={colors.primary} 
                strokeWidth={3}
                dot={{ fill: colors.primary, r: 5 }}
                activeDot={{ r: 7, fill: colors.secondary }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Cards de Resumo */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: getCardBackground(),
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${colors.primary}33`,
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Meta Anual
                </Typography>
                <TrendingUpIcon sx={{ color: colors.primary, fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                {formatarMoeda(metaAnual)}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: colors.progress.background,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: colors.progress.fill,
                  }
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: getCardBackground(),
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${colors.primary}33`,
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  VGV Acumulado
                </Typography>
                <AssessmentIcon sx={{ color: colors.primary, fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                {formatarMoeda(acumulado)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(percentualMeta, 100)} 
                  sx={{ 
                    flex: 1,
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: colors.progress.background,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.progress.fill,
                    }
                  }} 
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', minWidth: 50, textAlign: 'right' }}>
                  {percentualMeta.toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {percentualMeta.toFixed(1)}% da meta anual
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: getCardBackground(),
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${colors.primary}33`,
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  VGV {mesSelecionado}
                </Typography>
                <AttachMoneyIcon sx={{ color: colors.primary, fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                {formatarMoeda(vgvMes)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(percentualMes, 100)} 
                  sx={{ 
                    flex: 1,
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: colors.progress.background,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.progress.fill,
                    }
                  }} 
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', minWidth: 50, textAlign: 'right' }}>
                  {percentualMes.toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {percentualMes.toFixed(1)}% da meta anual
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: getCardBackground(),
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${colors.primary}33`,
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Corretores Ativos
                </Typography>
                <PeopleIcon sx={{ color: colors.primary, fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                {corretoresAtivos.length}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {corretoresComVendas.length} com vendas no mês
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top 3 Destaque - Só mostra se não houver corretor selecionado */}
      {top3.length > 0 && !corretorSelecionado && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            backgroundColor: 'background.paper', 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 3,
            background: getCardBackground(),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <EmojiEventsIcon sx={{ color: getPosicaoBadgeColor(1), fontSize: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Top 3 Performers
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {top3.map((corretor, index) => {
              const posicao = index + 1;
              const percentualCorretor = metaAnual > 0 ? (corretor.vgv / metaAnual) * 100 : 0;
              return (
                <Grid item xs={12} md={4} key={corretor.id}>
                  <Card
                    sx={{
                      p: 2,
                      background: getCardBackgroundReverse(),
                      border: `1px solid ${colors.border.primary}`,
                      borderRadius: 3,
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {posicao === 1 ? (
                        // 1º lugar sem badge, apenas avatar destacado
                        <Avatar 
                          src={corretor.foto || undefined} 
                          alt={corretor.nome} 
                          sx={{ 
                            width: 64, 
                            height: 64,
                            border: `3px solid ${getPosicaoBadgeColor(1)}`,
                            boxShadow: `0 0 15px ${getPosicaoBadgeColor(1)}40`,
                          }}
                        >
                          {!corretor.foto && corretor.nome 
                            ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                            : ''}
                        </Avatar>
                      ) : (
                        // 2º e 3º lugar com badge
                        <>
                          <Chip
                            label={`${posicao}º`}
                            sx={{
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              width: 50,
                              height: 50,
                            }}
                          />
                          <Avatar 
                            src={corretor.foto || undefined} 
                            alt={corretor.nome} 
                            sx={{ width: 56, height: 56 }}
                          >
                            {!corretor.foto && corretor.nome 
                              ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                              : ''}
                          </Avatar>
                        </>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                          {corretor.nome}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatarMoeda(corretor.vgv)}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(percentualCorretor, 100)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.4)', // More visible white background (40% opacity)
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                      {percentualCorretor.toFixed(2)}% da meta anual
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Tabela Completa de Ranking */}
      <Paper 
        sx={{ 
          background: getCardBackground(),
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: `1px solid ${colors.border.primary}`,
            background: getCardBackgroundReverse(),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {corretorSelecionado 
              ? `Relatório Individual - ${corretoresComVendas[0]?.nome || 'Corretor'}`
              : `Ranking Completo - ${mesSelecionado} ${anoSelecionado}`
            }
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {corretorSelecionado 
              ? `Relatório detalhado do corretor para ${mesSelecionado} ${anoSelecionado}`
              : `${corretoresComVendas.length} corretores com vendas registradas`
            }
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: `${colors.primary}25` }}>
                {!corretorSelecionado && (
                  <TableCell sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>Posição</TableCell>
                )}
                <TableCell sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>Corretor</TableCell>
                <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>VGV {mesSelecionado} </TableCell>
                <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>Agenciamentos</TableCell>
                <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>% da Meta</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '0.875rem' }}>Progresso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {corretoresComVendas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={corretorSelecionado ? 5 : 6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {corretorSelecionado 
                          ? 'Nenhuma venda registrada para este corretor no período selecionado'
                          : 'Nenhuma venda registrada para este período'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  corretoresComVendas.map((corretor, index) => {
                    const posicao = index + 1;
                    const percentualCorretor = metaAnual > 0 ? (corretor.vgv / metaAnual) * 100 : 0;
                    const isTop3 = posicao <= 3 && !corretorSelecionado;
                  return (
                    <TableRow
                      key={corretor.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.hover.light,
                        },
                        ...(isTop3 && {
                          backgroundColor: `${getPosicaoBadgeColor(posicao)}15`,
                        })
                      }}
                    >
                      {!corretorSelecionado && (
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              minWidth: 50,
                              height: 28,
                              borderRadius: '16px',
                              padding: '0 12px',
                              boxSizing: 'border-box',
                            }}
                          >
                            {posicao}º
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar 
                            src={corretor.foto || undefined} 
                            alt={corretor.nome} 
                            sx={{ width: 40, height: 40 }}
                          >
                            {!corretor.foto && corretor.nome 
                              ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                              : ''}
                          </Avatar>
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: isTop3 ? 'bold' : 'normal' }}>
                            {corretor.nome}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '1rem' }}>
                        {formatarMoeda(corretor.vgv)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: '1rem' }}>
                        {corretor.agenciamento || 0}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {percentualCorretor.toFixed(2)}%
                      </TableCell>
                      <TableCell>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(percentualCorretor, 100)} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.4)', // More visible white background (40% opacity)
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                            }
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              </TableBody>
            {corretoresComVendas.length > 0 && !corretorSelecionado && (
              <TableFooter>
                <TableRow sx={{ backgroundColor: `${colors.primary}25` }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1rem' }}>
                    Total Geral
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1rem' }}>
                    {formatarMoeda(vgvMes)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1rem' }}>
                    {totalAgenciamentos}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1rem' }}>
                    {percentualMes.toFixed(2)}%
                  </TableCell>
                  <TableCell>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(percentualMes, 100)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.4)', // More visible white background (40% opacity)
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.progress.fill,
                          borderRadius: 4,
                        }
                      }} 
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>

      {/* Estatísticas Adicionais */}
      {corretoresComVendas.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 3, mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 2.5,
                height: '100%',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper', 
                border: `1px solid ${colors.border.primary}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                Média de VGV por Corretor
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                {formatarMoeda(mediaVGV)}
              </Typography>
              <Box sx={{ flex: 1 }} /> {/* Spacer para manter altura consistente */}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 2.5,
                height: '100%',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper', 
                border: `1px solid ${colors.border.primary}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                Maior VGV do Mês
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                {formatarMoeda(corretoresComVendas[0]?.vgv || 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {corretoresComVendas[0]?.nome || ''}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 2.5,
                height: '100%',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper', 
                border: `1px solid ${colors.border.primary}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                Taxa de Participação
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                {corretoresAtivos.length > 0 
                  ? ((corretoresComVendas.length / corretoresAtivos.length) * 100).toFixed(1)
                  : 0}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {corretoresComVendas.length} de {corretoresAtivos.length} corretores
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Rodapé do Relatório */}
      <Box sx={{ mt: 4, pt: 2, borderTop: `1px solid ${colors.border.primary}`, '@media print': { mt: 2 } }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          Este relatório foi gerado automaticamente pelo Sistema Maratona Prime Class.
          Dados atualizados em tempo real.
        </Typography>
      </Box>
    </Box>
  );
}

export default Relatorio;
